import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Gauge, Pencil } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const STATUS_COLOR = { ok: "default", alert: "secondary", exceeded: "destructive" };

export default function Governance() {
  const { t } = useTranslation();
  const { currentOrg } = useOrg();
  const [summary, setSummary] = useState(null);
  const [budget, setBudget] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ monthly_budget_usd: 50, hard_limit: true, soft_alert_pct: 80 });

  const load = () => {
    api.get("/governance/usage").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/governance/budget").then((r) => { setBudget(r.data); setForm(r.data); }).catch(() => {});
  };
  useEffect(() => { if (currentOrg) load(); }, [currentOrg]);

  const save = async () => {
    try {
      await api.put("/governance/budget", {
        monthly_budget_usd: Number(form.monthly_budget_usd),
        hard_limit: form.hard_limit,
        soft_alert_pct: Number(form.soft_alert_pct),
      });
      setOpen(false);
      load();
      toast.success("Budget updated");
    } catch (e) { toast.error(apiError(e)); }
  };

  const b = summary?.budget;

  return (
    <div>
      <PageHeader
        title={t("m2.govTitle")}
        subtitle={t("m2.govSub")}
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-sm" data-testid="edit-budget-button"><Pencil size={16} className="mr-2" />{t("m2.editBudget")}</Button>
            </DialogTrigger>
            <DialogContent className="backdrop-blur-xl bg-background/80">
              <DialogHeader><DialogTitle className="font-heading">{t("m2.editBudget")}</DialogTitle><DialogDescription className="sr-only">Set the org budget</DialogDescription></DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2"><Label>{t("m2.budget")} (USD)</Label><Input type="number" value={form.monthly_budget_usd} onChange={(e) => setForm({ ...form, monthly_budget_usd: e.target.value })} className="rounded-sm font-mono" data-testid="budget-amount-input" /></div>
                <div className="space-y-2"><Label>{t("m2.softAlert")}</Label><Input type="number" value={form.soft_alert_pct} onChange={(e) => setForm({ ...form, soft_alert_pct: e.target.value })} className="rounded-sm font-mono" data-testid="budget-alert-input" /></div>
                <div className="flex items-center gap-2"><Switch checked={form.hard_limit} onCheckedChange={(v) => setForm({ ...form, hard_limit: v })} data-testid="budget-hardlimit-switch" /><Label>{t("m2.hardLimit")}</Label></div>
              </div>
              <DialogFooter><Button onClick={save} data-testid="save-budget-button">{t("common.save")}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="grid md:grid-cols-3 gap-4 mb-8">
        <div className="bg-card border border-border p-6 rounded-sm">
          <span className="overline text-muted-foreground">{t("m2.spent")}</span>
          <div className="mt-3 text-3xl font-heading font-black tracking-tighter" data-testid="usage-spent">${b?.spent ?? "0"}</div>
          <div className="text-xs text-muted-foreground font-mono mt-1">/ ${b?.budget ?? "0"}</div>
        </div>
        <div className="bg-card border border-border p-6 rounded-sm">
          <span className="overline text-muted-foreground">{t("m2.status")}</span>
          <div className="mt-3">
            <Badge variant={STATUS_COLOR[b?.status] || "secondary"} className="rounded-sm uppercase" data-testid="usage-status">{b?.status || "—"}</Badge>
          </div>
          <Progress value={Math.min(b?.pct || 0, 100)} className="mt-4 h-2" />
          <div className="text-xs text-muted-foreground font-mono mt-1">{b?.pct ?? 0}% used</div>
        </div>
        <div className="bg-card border border-border p-6 rounded-sm">
          <span className="overline text-muted-foreground">{t("m2.hardLimit")}</span>
          <div className="mt-3"><Gauge size={28} className="text-primary" weight="bold" /></div>
          <div className="text-sm mt-2 font-medium">{budget?.hard_limit ? "Enforced" : "Advisory"} · alert @ {budget?.soft_alert_pct}%</div>
        </div>
      </div>

      <div className="bg-card border border-border rounded-sm overflow-x-auto">
        <div className="px-6 py-4 border-b border-border"><h3 className="font-heading font-bold tracking-tight">Usage breakdown (this month)</h3></div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("m2.capability")}</TableHead>
              <TableHead>{t("m2.provider")}</TableHead>
              <TableHead>{t("m2.calls")}</TableHead>
              <TableHead>{t("m2.tokens")}</TableHead>
              <TableHead className="text-right">{t("m2.cost")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(summary?.breakdown || []).map((r, i) => (
              <TableRow key={i} data-testid={`usage-row-${r.capability}`}>
                <TableCell className="font-mono text-xs">{r.capability}</TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground">{r.provider}</TableCell>
                <TableCell className="font-mono text-xs">{r.calls}</TableCell>
                <TableCell className="font-mono text-xs">{r.tokens_in + r.tokens_out}</TableCell>
                <TableCell className="text-right font-mono text-xs">${r.cost}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
